import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from '@/utils/firebase';
import { LocationByRadius, LocationData } from '@/components/business/onboarding/types/locationTypes';
import { calculateDistance } from '@/utils/locationUtils';

// Define specific interface for raw location data
export interface RawLocationData extends LocationData {
  // zipCodes is now optional in LocationData, so we don't need to redefine it here
}

export interface LocationDataFetchingResult {
  rawLocations: RawLocationData[];
  locations: LocationByRadius[];
  filteredLocations: LocationByRadius[];
  isLoading: boolean;
  error: Error | null;
}

// Mock data as fallback
const MOCK_LOCATIONS = [
  {
    id: "1",
    name: "San Francisco",
    state: "California",
    stateAbr: "CA",
    coordinates: { latitude: 37.7749, longitude: -122.4194 },
    zipCodes: ["94102", "94103", "94104"]
  },
  {
    id: "2",
    name: "Oakland",
    state: "California",
    stateAbr: "CA",
    coordinates: { latitude: 37.8044, longitude: -122.2711 },
    zipCodes: ["94601", "94602", "94603"]
  },
  {
    id: "3",
    name: "San Jose",
    state: "California", 
    stateAbr: "CA",
    coordinates: { latitude: 37.3382, longitude: -121.8863 },
    zipCodes: ["95110", "95111", "95112"]
  },
  {
    id: "4",
    name: "Los Angeles",
    state: "California", 
    stateAbr: "CA",
    coordinates: { latitude: 34.0522, longitude: -118.2437 },
    zipCodes: ["90001", "90002", "90003"]
  },
  {
    id: "5",
    name: "Sacramento",
    state: "California", 
    stateAbr: "CA",
    coordinates: { latitude: 38.5816, longitude: -121.4944 },
    zipCodes: ["95811", "95814", "95816"]
  }
];

export const useLocationDataFetching = (): LocationDataFetchingResult => {
  const [rawLocations, setRawLocations] = useState<RawLocationData[]>([]);
  const [locations, setLocations] = useState<LocationByRadius[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<LocationByRadius[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Prevent duplicate fetch operations
  const hasFetchedRef = useRef(false);

  // Fetch locations data from JSON file
  useEffect(() => {
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    const fetchLocationsData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log('Attempting to fetch locations from @/data/locations.json...');
        const locationsModule = await import('@/data/locations.json');
        const jsonData = locationsModule.default;

        if (Array.isArray(jsonData) && jsonData.length > 0) {
          console.log(`Successfully loaded ${jsonData.length} locations from JSON file.`);
          // console.log('Sample data from JSON:', jsonData[0]); // Optional: for debugging

          const cleanData = jsonData.map(item => {
            const firstZipCode = Array.isArray(item.zipCodes) && item.zipCodes.length > 0
              ? item.zipCodes[0]
              : '';
            return {
              id: item.id || '',
              name: item.name || '',
              state: item.state || '',
              stateAbr: item.stateAbr || '',
              coordinates: {
                latitude: item.coordinates?.latitude || 0,
                longitude: item.coordinates?.longitude || 0
              },
              zipCodes: Array.isArray(item.zipCodes) ? [...item.zipCodes] : [],
              zip: firstZipCode, // Ensure zip is populated if available
              googleCriteriaId: item.googleCriteriaId, // Added
              targetType: item.targetType // Added
            };
          });

          console.log(`Processed ${cleanData.length} locations from JSON file`);
          setRawLocations(cleanData as RawLocationData[]); // Cast to ensure type compatibility
          const initialized = initializeLocations(cleanData as RawLocationData[]); // Cast to ensure type compatibility
          setLocations(initialized);
          // Note: filteredLocations will be set by the consuming component or context based on actual business coordinates and radius
          setFilteredLocations([]); // Initialize as empty, to be populated by consumer
        } else {
          console.warn('@/data/locations.json data is not an array or is empty.', jsonData);
          setError(new Error('Master location data is invalid or empty.'));
          setRawLocations([]);
          setLocations([]);
          setFilteredLocations([]);
        }
      } catch (jsonError) {
        console.error('CRITICAL: Failed to load master location data from @/data/locations.json. Location-based features will not work correctly.', jsonError);
        setError(jsonError instanceof Error ? jsonError : new Error('Failed to load master location data.'));
        setRawLocations([]);
        setLocations([]);
        setFilteredLocations([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLocationsData();
  }, []); // Empty dependency array ensures this only runs once
  
  // Separate function to initialize locations to keep the useEffect cleaner
  const initializeLocations = useCallback((dataToInitialize: RawLocationData[]): LocationByRadius[] => {
    console.log('Initializing locations from data:', dataToInitialize.length);
    if (!dataToInitialize || dataToInitialize.length === 0) {
      return [];
    }
    const initialized = dataToInitialize
      .filter(loc => loc.coordinates && typeof loc.coordinates.latitude === 'number' && typeof loc.coordinates.longitude === 'number')
      .map((loc): LocationByRadius => {
        const zipValue = loc.zip || (Array.isArray(loc.zipCodes) && loc.zipCodes.length > 0 ? loc.zipCodes[0] : '');
        return {
          id: loc.id,
          name: loc.name,
          state: loc.state || '',
          stateAbr: loc.stateAbr || '',
          coordinates: { // Ensure coordinates are valid numbers
            latitude: loc.coordinates?.latitude ?? 0,
            longitude: loc.coordinates?.longitude ?? 0,
          },
          zipCodes: loc.zipCodes || [],
          zip: zipValue,
          googleCriteriaId: loc.googleCriteriaId, // Added
          targetType: loc.targetType, // Added
          serviceable: true, // Default to true
          targeted: false,   // Default to false
          distance: undefined, // Will be calculated later
        };
      });
    console.log(`Initialized ${initialized.length} locations`);
    if (initialized.length > 0) {
      console.log('[useLocationDataFetching] initializeLocations - First initialized item:', JSON.stringify(initialized[0], null, 2));
    }
    return initialized;
  }, []);

  // Note: filterLocationsByRadius is removed from here.
  // Filtering logic will be handled by the consuming context/component
  // based on the dynamically fetched business coordinates and selected radius.

  return {
    rawLocations,
    locations,
    filteredLocations,
    isLoading,
    error
  };
};
